import type { ComponentProps } from "react";
import { useTranslation } from "react-i18next";

import { filterActivityForScriptReview } from "@/features/marketplace/lib/order-activity";
import {
  orderLineVideoDisplayName,
  orderLineVideos,
} from "@/features/marketplace/lib/order-line-videos";
import type {
  OrderScriptPreflightComment,
  OrderScriptRow,
} from "@/features/marketplace/types/marketplace.types";

import { OrderScriptActivityLog } from "./OrderScriptActivityLog";

type OrderScriptActivityLogProps = ComponentProps<typeof OrderScriptActivityLog>;

export function OrderScriptVideoActivitySections({
  script,
  entries,
  viewer,
  showFilters,
  collapseSystemRuns,
  defaultFilter,
  className,
  onReactionChange,
  isReactionPending,
}: {
  script: OrderScriptRow;
  entries: readonly OrderScriptPreflightComment[];
  viewer?: OrderScriptActivityLogProps["viewer"];
  showFilters?: boolean;
  collapseSystemRuns?: boolean;
  defaultFilter?: OrderScriptActivityLogProps["defaultFilter"];
  className?: string;
  onReactionChange?: OrderScriptActivityLogProps["onReactionChange"];
  isReactionPending?: OrderScriptActivityLogProps["isReactionPending"];
}) {
  const { t } = useTranslation();
  const videos = orderLineVideos(script);

  if (videos.length <= 1) {
    const scriptReviewId = videos[0]?.scriptReviewId ?? script.scriptReviewId;
    const scoped = filterActivityForScriptReview(entries, scriptReviewId, {
      lineLevelWhenUnscoped: true,
    });
    return (
      <OrderScriptActivityLog
        entries={scoped}
        viewer={viewer}
        showFilters={showFilters}
        collapseSystemRuns={collapseSystemRuns}
        defaultFilter={defaultFilter}
        className={className}
        onReactionChange={onReactionChange}
        isReactionPending={isReactionPending}
      />
    );
  }

  return (
    <div className={className ? `flex flex-col gap-6 ${className}` : "flex flex-col gap-6"}>
      {videos.map((video) => {
        const label = orderLineVideoDisplayName(video, t);
        const scoped = filterActivityForScriptReview(entries, video.scriptReviewId);
        return (
          <section
            key={video.id ?? `position-${video.position}`}
            className="flex flex-col gap-2"
            aria-label={t("marketplace.orders.activity.videoSection", {
              label,
              defaultValue: "Activity for {{label}}",
            })}
          >
            <h3 className="text-xs font-medium text-muted-foreground">{label}</h3>
            {scoped.length > 0 ? (
              <OrderScriptActivityLog
                entries={scoped}
                viewer={viewer}
                showFilters={showFilters}
                collapseSystemRuns={collapseSystemRuns}
                defaultFilter={defaultFilter}
                onReactionChange={onReactionChange}
                isReactionPending={isReactionPending}
              />
            ) : (
              <p className="text-drawer-body text-muted-foreground">
                {t("marketplace.orders.list.panel.activityEmpty")}
              </p>
            )}
          </section>
        );
      })}
    </div>
  );
}
